import React, { useEffect, useRef, useState } from "react";
import { MessageCircleQuestion, X, Send, Loader2, RotateCcw, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { streamSSE } from "@/lib/sse";
import { HelperQuiz } from "@/components/HelperQuiz";
import { ListenButton } from "@/components/ListenButton";

const newSession = () => (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9-]/g, "");
const STARTERS = ["Can you explain this lesson more simply?", "Give me a quick example to practice", "What should I focus on this week?"];

const Bubble = ({ m, i, busy }) => (
  <div className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`} data-testid={`helper-msg-${i}`}>
    <p className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
      {m.content || <Loader2 size={14} className="animate-spin" />}
    </p>
    {m.role === "assistant" && m.content && !busy && <ListenButton id={`helper-${i}`} text={m.content} label="Music Helper answer" />}
  </div>
);

// Floating lesson chat: AI music theory helper for course owners.
export const LessonHelper = ({ lesson, courseKey }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [session, setSession] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [quiz, setQuiz] = useState(false);
  const end = useRef(null);
  const context = (lesson.sections || []).map((s) => `${s.heading}: ${s.text || ""}`).join("\n").slice(0, 7500);

  useEffect(() => {
    if (!open || session) return;
    api.get(`/helper/${lesson.id}/history`, { params: { course_key: courseKey } })
      .then(({ data }) => { setSession(data.session_id || newSession()); setMsgs(data.messages || []); })
      .catch((e) => { setSession(newSession()); setError(e.response?.data?.detail || ""); });
  }, [open, session, lesson.id, courseKey]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    setText(""); setError(""); setBusy(true); setQuiz(false);
    setMsgs((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    try {
      await streamSSE(`/helper/${lesson.id}/chat`, { message: question, session_id: session, course_key: courseKey, lesson_title: lesson.title, lesson_context: context },
        (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }));
    } catch (e) {
      setError(e.message);
      setMsgs((m) => (m[m.length - 1]?.content ? m : m.slice(0, -1)));
    } finally { setBusy(false); }
  };
  const reset = () => { setSession(newSession()); setMsgs([]); setError(""); setQuiz(false); };

  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="helper-open"
      className="fixed bottom-6 right-[84px] z-40 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5">
      <MessageCircleQuestion size={18} /> <span className="sm:hidden">Ask</span><span className="hidden sm:inline">Ask the Music Helper</span>
    </button>
  );
  return (
    <div className="fixed inset-x-3 bottom-3 z-[120] flex max-h-[75vh] flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-2xl sm:inset-x-auto sm:right-5 sm:w-[400px]" data-testid="helper-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <p className="font-serif text-lg leading-tight">Music Helper</p>
          <p className="text-xs text-muted-foreground">AI answers about this lesson. Check anything important with Jason.</p>
        </div>
        <div className="flex gap-1">
          <button onClick={reset} title="New chat" aria-label="New chat" data-testid="helper-new-chat" className="rounded-full p-2 hover:bg-secondary"><RotateCcw size={16} /></button>
          <button onClick={() => setOpen(false)} aria-label="Close helper" data-testid="helper-close" className="rounded-full p-2 hover:bg-secondary"><X size={16} /></button>
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="helper-messages">
        {quiz && <HelperQuiz lessonId={lesson.id} body={{ course_key: courseKey, lesson_title: lesson.title, lesson_context: context }} onClose={() => setQuiz(false)} />}
        {!quiz && <button onClick={() => setQuiz(true)} data-testid="helper-quiz-open"
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary/50 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/20">
          <ListChecks size={15} /> Quiz me: 5 practice questions
        </button>}
        {!quiz && !msgs.length && (
          <div className="space-y-2" data-testid="helper-starters">
            <p className="text-sm text-muted-foreground">Ask anything about “{lesson.title}”.</p>
            {STARTERS.map((s, i) => (
              <button key={s} onClick={() => ask(s)} data-testid={`helper-starter-${i}`} className="block w-full rounded-xl border border-border px-3 py-2 text-left text-sm hover:border-primary">{s}</button>
            ))}
          </div>
        )}
        {!quiz && msgs.map((m, i) => <Bubble key={i} m={m} i={i} busy={busy && i === msgs.length - 1} />)}
        {error && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="helper-error">{error}</p>}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 border-t border-border p-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} placeholder="Type your question…" data-testid="helper-input"
          className="min-w-0 flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm focus:border-primary focus:outline-none" />
        <button type="submit" disabled={busy || !text.trim() || !session} aria-label="Send" data-testid="helper-send"
          className="rounded-full bg-primary p-2.5 text-primary-foreground disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
};
